import { useTranslation, setLanguage, getLanguages, getLangName } from "@/lib/i18n";
import ValueBetsToday from "@/components/betmodel/ValueBetsToday";
import PricingPage from "@/components/betmodel/PricingPage";
import AdminFeedback from "@/components/betmodel/AdminFeedback";
import Terms from "@/components/betmodel/Terms";
import Privacy from "@/components/betmodel/Privacy";
import Refund from "@/components/betmodel/Refund";
import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import StandingsTable from "@/components/betmodel/StandingsTable";
import LegalDisclaimer from "@/components/betmodel/LegalDisclaimer";
import LeagueMenu from "@/components/betmodel/LeagueMenu";
import DayCalendar from "@/components/betmodel/DayCalendar";
import FixturesList from "@/components/betmodel/FixturesList";
import MatchPrediction from "@/components/betmodel/MatchPrediction";
import BankrollManager from "@/components/betmodel/BankrollManager";
import AuthModal from "@/components/betmodel/AuthModal";
import ApiStatusBar from "@/components/betmodel/ApiStatusBar";
import DownloadPage from "@/components/betmodel/DownloadPage";
import TopTips from "@/components/betmodel/TopTips";
import FeedbackModal from "@/components/betmodel/FeedbackModal";
import {
  BarChart3,
  Wallet,
  RefreshCw,
  Loader2,
  Zap,
  AlertCircle,
  User,
  Users,
  LogOut,
  Download,
  Star,
  MessageSquare,
} from "lucide-react";

const TABS = { FIXTURES: "fixtures", BANKROLL: "bankroll", TIPS: "tips" };

function AppContent() {
  const { user, loading: authLoading, logout } = useAuth();

  const [legalAccepted, setLegalAccepted] = useState(
    () => localStorage.getItem("betmodel_legal_accepted") === "true"
  );
  const [showAuth, setShowAuth] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const [leagues, setLeagues] = useState([]);
  const [activeLeague, setActiveLeague] = useState("POR");
  const [filterByLeague, setFilterByLeague] = useState(false);
  const [fixtures, setFixtures] = useState([]);
  // Reset language preference to English default
  if (!localStorage.getItem("betmodel_lang")) {
    localStorage.setItem("betmodel_lang", "en");
  }
  const { t, lang } = useTranslation();
  const [prediction, setPrediction] = useState(null);
  const [predictionLeagueCode, setPredictionLeagueCode] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.FIXTURES);
  const [bankroll, setBankroll] = useState(1000);
  const [kellyFraction, setKellyFraction] = useState(0.5);
  const [showValueBets, setShowValueBets] = useState(false);
  const [valueBetsDate, setValueBetsDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [loadingFixture, setLoadingFixture] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
 const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailyFixtures, setDailyFixtures] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [showStandings, setShowStandings] = useState(false);
  const [activePage, setActivePage] = useState(null);
  const [showAdminFeedback, setShowAdminFeedback] = useState(false);
  // Sync bankroll from user
  useEffect(() => {
    if (user && user.bankroll) {
      setBankroll(user.bankroll);
      setKellyFraction(user.kelly_fraction || 0.5);
    }
  }, [user]);

  useEffect(() => {
    api.getLeagues()
      .then((data) => setLeagues(data.leagues || []))
      .catch(() => setError("Erro ao carregar ligas"));
  }, []);

  const fetchFixtures = useCallback(async (leagueCode) => {
    setLoading(true);
    setError(null);
    setPrediction(null);
    try {
      const data = await api.getFixtures(leagueCode);
      setFixtures(data.fixtures || []);
    } catch {
      setError("Erro ao carregar jogos. A API pode estar limitada.");
      setFixtures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (legalAccepted && activeLeague) fetchFixtures(activeLeague);
  }, [legalAccepted, activeLeague, fetchFixtures]);

  const handleLeagueSelect = (code) => {
    setActiveLeague(code);
    setFilterByLeague(true);
    setActiveTab(TABS.FIXTURES);
    setPrediction(null);
    fetchFixtures(code);
  };

  const handleFixtureSelect = async (fixtureId, leagueCode) => {
    setLoadingFixture(fixtureId);
    setError(null);
    if (leagueCode) setPredictionLeagueCode(leagueCode);
    try {
      const data = await api.getPrediction(fixtureId, bankroll, kellyFraction);
      setPrediction(data);
    } catch {
      setError("Erro ao gerar previsao.");
    } finally {
      setLoadingFixture(null);
    }
  };
const fetchDailyFixtures = useCallback(async (date) => {
    setLoadingDaily(true);
    const d = new Date(date);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    try {
      const allFixtures = [];
      for (const league of leagues) {
        try {
          const data = await api.getFixtures(league.code);
          const filtered = (data.fixtures || []).filter(f => {
            if (!f.date) return false;
            const fd = new Date(f.date);
            fd.setHours(fd.getHours() + 1); // UTC+1 Lisboa
            const fStr = `${fd.getFullYear()}-${String(fd.getMonth()+1).padStart(2,'0')}-${String(fd.getDate()).padStart(2,'0')}`;
            return fStr === dateStr;
          }).map(f => ({ ...f, league_code: league.code, league_name: league.name }));
          allFixtures.push(...filtered);
        } catch { /* silent */ }
      }
      setDailyFixtures(allFixtures);
    } catch { /* silent */ }
    finally { setLoadingDaily(false); }
  }, [leagues]);

  useEffect(() => {
  if (prediction) {
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 200);
  }
}, [prediction]);
  useEffect(() => {
    if (leagues.length > 0) fetchDailyFixtures(selectedDate);
  }, [selectedDate, leagues, fetchDailyFixtures]);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.refreshLeagueData(activeLeague);
      await fetchFixtures(activeLeague);
    } catch { /* silent */ }
    finally { setRefreshing(false); }
  };

  if (!legalAccepted) {
    const handleAccept = () => {
      localStorage.setItem("betmodel_legal_accepted", "true");
      setLegalAccepted(true);
    };
    return <LegalDisclaimer onAccept={handleAccept} />;
  }

  if (activePage === 'terms') return <div><button onClick={() => setActivePage(null)} className="p-4 text-volt">← Voltar</button><Terms /></div>;
  if (activePage === 'privacy') return <div><button onClick={() => setActivePage(null)} className="p-4 text-volt">← Voltar</button><Privacy /></div>;
  if (activePage === 'refund') return <div><button onClick={() => setActivePage(null)} className="p-4 text-volt">← Voltar</button><Refund /></div>;
  
  return (
    <div className="min-h-screen bg-obsidian text-white font-body">
      {/* Auth Modal */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {/* Feedback Modal */}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} userEmail={user?.email} />}

      {/* Header */}
      <header className="border-b border-line bg-surface/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-volt rounded-sm flex items-center justify-center">
              <Zap className="w-5 h-5 text-obsidian" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-black uppercase tracking-tight leading-none">
                BetModel
              </h1>
              <p className="text-[10px] text-txt-secondary uppercase tracking-[0.2em]">
                v8.2 Dixon-Coles
              </p>
            </div>
          </div>

          <nav className="flex items-center gap-1">
            <button
              data-testid="nav-fixtures"
              onClick={() => { setActiveTab(TABS.FIXTURES); setPrediction(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                activeTab === TABS.FIXTURES ? "bg-volt/10 text-volt" : "text-txt-secondary hover:text-white"
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('navFixtures')}</span>
            </button>
            <button
              data-testid="nav-bankroll"
              onClick={() => { setActiveTab(TABS.BANKROLL); setPrediction(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                activeTab === TABS.BANKROLL ? "bg-volt/10 text-volt" : "text-txt-secondary hover:text-white"
              }`}
            >
              <Wallet className="w-4 h-4" />
              <span className="hidden sm:inline">{t('navBankroll')}</span>
            </button>
          </nav>

          <div className="flex items-center gap-2">
            {/* Language Selector */}
<select
  value={lang}
  onChange={(e) => setLanguage(e.target.value)}
  className="bg-surface border border-line rounded-sm text-txt-secondary text-xs px-2 py-1.5 hover:border-volt transition-colors cursor-pointer"
>
  {getLanguages().map(l => (
    <option key={l} value={l}>{getLangName(l)}</option>
  ))}
</select>
{/* Affiliate Button */}
<button
  onClick={() => window.open('https://whop.com/betmodel/affiliates', '_blank')}
  className="flex items-center gap-1.5 px-3 py-2 bg-surface border border-line rounded-sm text-txt-secondary text-xs font-bold uppercase tracking-wide hover:border-volt hover:text-volt transition-colors"
  title="Affiliate Program"
>
  <Users className="w-3.5 h-3.5" />
  <span className="hidden sm:inline">Earn 30%</span>
</button>
            {/* Feedback Button */}
            <button
              data-testid="feedback-btn"
              onClick={() => setShowFeedback(true)}
              className="p-2 bg-surface border border-line rounded-sm hover:bg-surface-hover transition-colors"
              title="Sugestoes"
            >
              <MessageSquare className="w-4 h-4 text-txt-secondary" />
            </button>

            {/* API Status */}
            <div className="hidden lg:block">
              <ApiStatusBar />
            </div>
            
            {/* Refresh */}
            <button
              data-testid="refresh-data-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 bg-surface border border-line rounded-sm hover:bg-surface-hover transition-colors"
              title="Atualizar dados"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin text-volt" : "text-txt-secondary"}`} />
            </button>

            {/* Auth */}
            {user ? (
              <div className="flex items-center gap-2">
                {user.role === 'admin' && (
                  <a
                    href="?download"
                    data-testid="download-btn"
                    className="p-2 bg-surface border border-line rounded-sm hover:bg-surface-hover transition-colors"
                    title="Descarregar projeto"
                  >
                    <Download className="w-4 h-4 text-txt-secondary" />
                  </a>
                )}
                {user?.role === 'admin' && (
  <button
    onClick={() => setShowAdminFeedback(!showAdminFeedback)}
    className="p-2 bg-surface border border-line rounded-sm hover:bg-surface-hover transition-colors"
    title="Ver Feedback"
  >
    <MessageSquare className="w-4 h-4 text-volt" />
  </button>
)}
                <span className="text-xs text-txt-secondary hidden md:inline">{user.name || user.email}</span>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="p-2 bg-surface border border-line rounded-sm hover:bg-surface-hover transition-colors"
                  title="Sair"
                >
                  <LogOut className="w-4 h-4 text-txt-secondary" />
                </button>
              </div>
            ) : (
              <button
                data-testid="login-btn"
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-volt/10 border border-volt/30 rounded-sm text-volt text-xs font-bold uppercase tracking-wide hover:bg-volt/20 transition-colors"
              >
                <User className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Entrar</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile API Status */}
      <div className="lg:hidden px-4 pt-3">
        <ApiStatusBar />
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {showAdminFeedback && user?.role === 'admin' && (
  <AdminFeedback onClose={() => setShowAdminFeedback(false)} />
)}
        <div className="flex items-center gap-3">
          <LeagueMenu
            leagues={leagues}
            activeLeague={activeLeague}
            onSelect={handleLeagueSelect}
            loading={loading}
          />
        </div>
        {!loading && activeTab === TABS.FIXTURES && !prediction && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setShowValueBets(false); }}
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${!showValueBets ? "bg-volt/10 border-volt text-volt" : "bg-surface border-line text-txt-secondary"}`}
              >
                ⭐ {t('topTips')}
              </button>
              <button
                onClick={() => { setShowValueBets(true); }}
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${showValueBets ? "bg-volt/10 border-volt text-volt" : "bg-surface border-line text-txt-secondary"}`}
              >
                📈 {t('valueBets')}
              </button>
            </div>
            {!showValueBets && <TopTips bankroll={bankroll} isAdmin={user && user.role === 'admin'} />}
           {showValueBets && <ValueBetsToday bankroll={bankroll} onFixtureSelect={handleFixtureSelect} selectedDate={selectedDate} isAdmin={user && user.role === 'admin'} />}
          </div>
        )}

{!prediction && (
          <>
            <DayCalendar
              selectedDate={selectedDate}
              onDateChange={(date) => { setSelectedDate(date); setFilterByLeague(false); setValueBetsDate(date); setShowValueBets(false); setTimeout(() => setShowValueBets(true), 100); }}
              dailyFixtures={filterByLeague ? fixtures.map(f => ({...f, league_name: activeLeague})) : dailyFixtures}
              loadingDaily={filterByLeague ? loading : loadingDaily}
              onFixtureSelect={handleFixtureSelect}
              loadingFixtureId={loadingFixture}
            />
            {showStandings && (
              <StandingsTable
                leagueCode={activeLeague}
                leagueName={leagues.find(l => l.code === activeLeague)?.name}
                onClose={() => setShowStandings(false)}
              />
            )}
          </>
        )}
        {error && (
          <div className="bg-danger/10 border border-danger/30 rounded-sm px-4 py-3 flex items-center gap-2 text-sm text-danger" data-testid="error-banner">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-volt animate-spin" />
          </div>
        )}

        {!loading && activeTab === TABS.FIXTURES && prediction && (
          <MatchPrediction prediction={prediction} onBack={() => setPrediction(null)} bankroll={bankroll} isAdmin={user && user.role === 'admin'} leagueCode={predictionLeagueCode || activeLeague} />
        )}

        {!loading && activeTab === TABS.BANKROLL && (
          <BankrollManager
            bankroll={bankroll}
            setBankroll={setBankroll}
            kellyFraction={kellyFraction}
            setKellyFraction={setKellyFraction}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-40">
        <div className="border-t border-line bg-obsidian/95 backdrop-blur-sm py-2 flex items-center justify-center gap-4">
          <button onClick={() => setActivePage('terms')} className="text-txt-secondary/50 text-[10px] uppercase tracking-widest hover:text-txt-secondary transition-colors">{t('terms')}</button>
          <span className="text-txt-secondary/20">·</span>
          <button onClick={() => setActivePage('privacy')} className="text-txt-secondary/50 text-[10px] uppercase tracking-widest hover:text-txt-secondary transition-colors">{t('privacy')}</button>
          <span className="text-txt-secondary/20">·</span>
          <button onClick={() => setActivePage('refund')} className="text-txt-secondary/50 text-[10px] uppercase tracking-widest hover:text-txt-secondary transition-colors">{t('refund')}</button>
        </div>
      </footer>
      <div className="h-12" />
    </div>
  );
}

function App() {
  // Legal pages
  if (window.location.pathname === '/pricing') return <PricingPage />;
  if (window.location.pathname === '/terms') return <Terms />;
  if (window.location.pathname === '/privacy') return <Privacy />;
  if (window.location.pathname === '/refund') return <Refund />;

  // Download page doesn't need auth
  if (window.location.search.includes("download")) {
    return <DownloadPage />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;


